import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

import {
  FaCompress,
  FaExpand,
  FaQrcode,
  FaSyncAlt,
  FaTrophy,
} from "react-icons/fa";

import { getCastingScreenBundle } from "../../services/eventLiveServices";

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, "")
    .trim();

const isValidUrl = (value = "") => {
  try {
    const url = new URL(String(value).trim());

    return ["http:", "https:"].includes(
      url.protocol
    );
  } catch {
    return false;
  }
};

const getSlots = (mat) => {
  const slots = Array.isArray(mat?.slots)
    ? mat.slots
    : Array.isArray(mat?.Slots)
    ? mat.Slots
    : [];

  return slots
    .filter(
      (slot) =>
        slot?.is_active !== false &&
        String(slot?.label || "").trim()
    )
    .sort(
      (a, b) =>
        Number(a.slot_index) -
        Number(b.slot_index)
    );
};

const getGridColumns = (
  count,
  width,
  height
) => {
  const portrait = height > width;

  if (count <= 1) return 1;
  if (count === 2) return portrait ? 1 : 2;
  if (count <= 4) return 2;
  if (count <= 6) return 3;
  if (count <= 8) return 4;
  if (count <= 10) return 5;
  if (count <= 12) return 4;
  if (count <= 15) return 5;

  return 6;
};

const getMessageTheme = (type) => {
  switch (String(type || "").toUpperCase()) {
    case "ALERT":
    case "CALL":
      return "from-rose-700 via-red-700 to-red-950";

    case "PODIUM":
      return "from-blue-700 via-indigo-700 to-indigo-950";

    default:
      return "from-slate-700 via-slate-900 to-black";
  }
};

function MatCard({ mat }) {
  const surfaceType =
    mat?.surface_type || "MAT";

  if (surfaceType === "CENTRAL_TABLE") {
    return (
      <article className="flex min-h-0 items-center justify-center rounded-[2rem] border-4 border-white/40 bg-white text-slate-950 shadow-2xl">
        <div className="text-center">
          <p className="text-[clamp(2rem,5vw,6rem)] font-black">
            TC
          </p>

          <p className="mt-2 text-[clamp(0.9rem,1.5vw,1.6rem)] font-black uppercase tracking-widest">
            Table centrale
          </p>
        </div>
      </article>
    );
  }

  if (surfaceType === "PODIUM") {
    return (
      <article className="flex min-h-0 items-center justify-center rounded-[2rem] border-4 border-white/40 bg-gradient-to-br from-amber-300 to-yellow-500 text-slate-950 shadow-2xl">
        <div className="text-center">
          <FaTrophy className="mx-auto text-[clamp(3rem,7vw,8rem)]" />

          <p className="mt-3 text-[clamp(1.4rem,3vw,3.5rem)] font-black uppercase">
            {mat.label || "Podium"}
          </p>
        </div>
      </article>
    );
  }

  const slots = getSlots(mat);

  return (
    <article className="flex min-h-0 flex-col overflow-hidden rounded-[2rem] border-4 border-yellow-100/70 bg-yellow-300 text-slate-950 shadow-2xl">
      <header className="bg-slate-950 px-4 py-3 text-white">
        <h2 className="truncate text-center text-[clamp(1.3rem,2.4vw,3rem)] font-black uppercase">
          {mat.label ||
            `Tapis ${mat.mat_number}`}
        </h2>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-2 p-3">
        {slots.length === 0 ? (
          <div className="flex flex-1 items-center justify-center rounded-2xl bg-white/60 text-center text-[clamp(1rem,1.8vw,2rem)] font-black">
            Aucune catégorie
          </div>
        ) : (
          slots.map((slot) => (
            <div
              key={slot.id || slot.slot_index}
              className="flex min-h-0 flex-1 flex-col justify-center rounded-2xl bg-white/90 px-3 py-2 text-center shadow-sm"
            >
              <p className="break-words text-[clamp(1.15rem,2.2vw,2.7rem)] font-black leading-tight">
                {slot.label}
              </p>

              {slot.note ? (
                <p className="mt-1 text-[clamp(0.75rem,1.1vw,1.2rem)] font-bold text-slate-600">
                  {slot.note}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
    </article>
  );
}

export default function CastingScreenPage() {
  const { eventId } = useParams();

  const [bundle, setBundle] =
    useState(null);
  const [loading, setLoading] =
    useState(true);
  const [connected, setConnected] =
    useState(true);
  const [lastUpdate, setLastUpdate] =
    useState(null);
  const [showQr, setShowQr] =
    useState(false);
  const [isFullscreen, setIsFullscreen] =
    useState(Boolean(document.fullscreenElement));

  const [viewport, setViewport] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  const refresh = useCallback(
    async ({ silent = false } = {}) => {
      if (!eventId) return;

      if (!silent) {
        setLoading(true);
      }

      try {
        const data =
          await getCastingScreenBundle(
            eventId
          );

        setBundle(data);
        setConnected(true);
        setLastUpdate(new Date());
      } catch (error) {
        console.error(error);
        setConnected(false);
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [eventId]
  );

  useEffect(() => {
    refresh();

    const interval = window.setInterval(
      () => {
        refresh({ silent: true });
      },
      5000
    );

    return () =>
      window.clearInterval(interval);
  }, [refresh]);

  useEffect(() => {
    const handleResize = () => {
      setViewport({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    const handleFullscreen = () => {
      setIsFullscreen(
        Boolean(document.fullscreenElement)
      );
    };

    window.addEventListener(
      "resize",
      handleResize
    );

    document.addEventListener(
      "fullscreenchange",
      handleFullscreen
    );

    return () => {
      window.removeEventListener(
        "resize",
        handleResize
      );

      document.removeEventListener(
        "fullscreenchange",
        handleFullscreen
      );
    };
  }, []);

  const event = bundle?.event;
  const state = bundle?.state;

  const visibleMats = useMemo(
    () =>
      (bundle?.mats || [])
        .filter(
          (mat) =>
            mat?.is_open !== false &&
            mat?.is_open !== 0
        )
        .sort(
          (a, b) =>
            (Number(a.sort_order) ||
              Number(a.mat_number)) -
            (Number(b.sort_order) ||
              Number(b.mat_number))
        ),
    [bundle?.mats]
  );

  const activeMessage = useMemo(() => {
    const messages = bundle?.messages || [];

    return (
      messages.find(
        (message) =>
          Number(message.id) ===
          Number(
            state?.active_message_id
          )
      ) ||
      messages.find(
        (message) => message.is_pinned
      ) ||
      messages[0] ||
      null
    );
  }, [
    bundle?.messages,
    state?.active_message_id,
  ]);

  const mode = state?.mode || "MATS";

  const followUrl = String(
    event?.suivi_event || ""
  ).trim();

  const hasQr = isValidUrl(followUrl);

  const columns = getGridColumns(
    visibleMats.length,
    viewport.width,
    viewport.height
  );

  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
      } else {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.error(error);
    }
  };

  if (loading && !bundle) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950 text-2xl font-black text-white">
        Chargement de l’écran…
      </div>
    );
  }

  return (
    <main className="fixed inset-0 z-[99999] flex flex-col overflow-hidden bg-slate-950 text-white">
      <header className="flex h-16 shrink-0 items-center justify-between gap-4 border-b border-white/10 bg-slate-950 px-4">
        <div className="min-w-0">
          <h1 className="truncate text-lg font-black sm:text-2xl">
            {event?.titre ||
              "Écran compétition"}
          </h1>

          <div className="mt-0.5 flex items-center gap-2 text-[11px] font-bold text-white/60">
            <span
              className={[
                "h-2 w-2 rounded-full",
                connected
                  ? "bg-emerald-400"
                  : "bg-rose-500",
              ].join(" ")}
            />

            <span>
              {connected
                ? "Synchronisé"
                : "Connexion interrompue"}
            </span>

            {lastUpdate ? (
              <span>
                •{" "}
                {lastUpdate.toLocaleTimeString(
                  "fr-FR"
                )}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() =>
              refresh({ silent: true })
            }
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            title="Actualiser"
          >
            <FaSyncAlt />
          </button>

          {hasQr ? (
            <button
              type="button"
              onClick={() =>
                setShowQr((value) => !value)
              }
              className="flex h-10 items-center gap-2 rounded-xl bg-white/10 px-3 text-xs font-black hover:bg-white/20"
            >
              <FaQrcode />
              QR
            </button>
          ) : null}

          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 hover:bg-white/20"
            title="Plein écran"
          >
            {isFullscreen ? (
              <FaCompress />
            ) : (
              <FaExpand />
            )}
          </button>
        </div>
      </header>

      {showQr && hasQr ? (
        <section className="flex min-h-0 flex-1 flex-col items-center justify-center bg-white p-6 text-slate-950">
          <h2 className="mb-6 text-center text-[clamp(1.5rem,3vw,3rem)] font-black uppercase tracking-widest">
            Suivi de la compétition
          </h2>

          <div className="h-[min(72vh,72vw)] w-[min(72vh,72vw)]">
            <QRCodeSVG
              value={followUrl}
              size={1600}
              level="M"
              includeMargin={false}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </div>
        </section>
      ) : mode === "MESSAGE" &&
        activeMessage ? (
        <section className="flex min-h-0 flex-1 items-center justify-center p-6">
          <article
            className={[
              "w-full max-w-7xl rounded-[3rem] bg-gradient-to-br p-8 shadow-2xl sm:p-12",
              getMessageTheme(
                activeMessage.type
              ),
            ].join(" ")}
          >
            <p className="text-[clamp(1rem,2vw,2rem)] font-black uppercase tracking-[0.25em] text-white/70">
              {activeMessage.type || "INFO"}
              {" • "}
              {activeMessage.target_mat_number
                ? `Surface ${activeMessage.target_mat_number}`
                : "Message global"}
            </p>

            <h2 className="mt-5 text-[clamp(2.4rem,6vw,7rem)] font-black leading-tight">
              {activeMessage.title ||
                "Information"}
            </h2>

            <p className="mt-7 whitespace-pre-wrap text-[clamp(1.7rem,4vw,4.5rem)] font-bold leading-snug">
              {stripHtml(
                activeMessage.content || ""
              )}
            </p>
          </article>
        </section>
      ) : (
        <section className="min-h-0 flex-1 bg-emerald-800 p-3">
          {visibleMats.length === 0 ? (
            <div className="flex h-full items-center justify-center text-3xl font-black">
              Aucune surface ouverte
            </div>
          ) : (
            <div
              className="grid h-full min-h-0 gap-3"
              style={{
                gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
                gridAutoRows: "minmax(0, 1fr)",
              }}
            >
              {visibleMats.map((mat) => (
                <MatCard
                  key={
                    mat.id || mat.mat_number
                  }
                  mat={mat}
                />
              ))}
            </div>
          )}
        </section>
      )}
    </main>
  );
}